import { Botao } from '@mymaxx/ui';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Download, ShieldAlert } from 'lucide-react';
import { useState } from 'react';
import { Link, Navigate } from 'react-router';
import { ErroApi, clienteApi } from '../lib/api';
import { useSessao } from '../lib/sessao';

/**
 * Privacidade e meus dados: exportar tudo o que guardamos e pedir a exclusão.
 *
 * Estes dois direitos são da LGPD e precisam existir — mas não precisam estar na primeira
 * tela. Ficavam lá, e um botão vermelho de "Solicitar exclusão" embaixo do histórico é
 * convite ao toque errado, ainda mais no celular (06/10). Aqui eles continuam a dois
 * toques de quem os procura, e fora do caminho de quem só quer falar com a MAXX.
 *
 * O técnico de campo não chega nesta tela: a conta dele é de trabalho, criada pela empresa
 * — pedir ali a exclusão dos próprios dados não faz sentido nenhum, e o caminho dele para
 * isso é o mesmo de qualquer funcionário.
 */
export function MeusDados() {
  const { cliente } = useSessao();
  const [aviso, setAviso] = useState<{ tipo: 'ok' | 'erro'; texto: string } | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const solicitacoes = useQuery({ queryKey: ['solicitacoes'], queryFn: clienteApi.solicitacoes });

  // Conta de trabalho não tem esta tela. A rota é protegida, mas quem digitar o endereço
  // na barra volta para a conta em vez de ver uma tela que não é dele.
  if (cliente?.tecnico) return <Navigate to="/conta" replace />;

  async function comAviso(acao: () => Promise<string>) {
    setOcupado(true);
    setAviso(null);
    try {
      setAviso({ tipo: 'ok', texto: await acao() });
    } catch (e) {
      setAviso({
        tipo: 'erro',
        texto: e instanceof ErroApi ? e.mensagem : 'Não consegui fazer isso.',
      });
    } finally {
      setOcupado(false);
    }
  }

  const pedirExclusao = () =>
    confirm(
      'Vamos analisar seu pedido e responder em até 15 dias. Registros de atendimento podem ser mantidos pelos prazos exigidos por lei. Deseja continuar?',
    ) &&
    void comAviso(async () => {
      await clienteApi.solicitarExclusao();
      await solicitacoes.refetch();
      return 'Pedido registrado. Responderemos em até 15 dias.';
    });

  return (
    <main className="mx-auto max-w-2xl space-y-6 px-5 py-8">
      <header className="flex items-center gap-3">
        <Link to="/conta" aria-label="Voltar" className="text-neutral-500">
          <ArrowLeft size={20} />
        </Link>
        <h1 className="text-lg font-semibold">Privacidade e meus dados</h1>
      </header>

      <section className="rounded-card space-y-3 border border-neutral-200 p-5 dark:border-neutral-800">
        <h2 className="text-sm font-medium">Baixar meus dados</h2>
        <p className="text-xs text-neutral-500">
          Um arquivo com tudo o que guardamos sobre você: cadastro, serviços e atendimentos.
        </p>
        <Botao
          variante="secundario"
          disabled={ocupado}
          onClick={() =>
            void comAviso(async () => {
              const r = await clienteApi.exportarDados('pdf');
              return r.agendada ? r.mensagem : 'Download iniciado.';
            })
          }
        >
          <Download size={16} /> Baixar meus dados
        </Botao>
      </section>

      <section className="rounded-card space-y-3 border border-neutral-200 p-5 dark:border-neutral-800">
        <h2 className="text-sm font-medium">Solicitar exclusão</h2>
        <p className="text-xs text-neutral-500">
          Analisamos e respondemos em até 15 dias. Registros de atendimento podem ser mantidos pelos
          prazos exigidos por lei — a resposta explica o que fica e por quê.
        </p>
        <Botao variante="perigo" disabled={ocupado} onClick={pedirExclusao}>
          <ShieldAlert size={16} /> Solicitar exclusão
        </Botao>
        {solicitacoes.data?.map((s) => (
          <p key={s.id} className="text-xs text-neutral-500">
            Pedido de {s.tipo} em {new Date(s.criadaEm).toLocaleDateString('pt-BR')} — {s.status},
            resposta até {new Date(s.prazoEm).toLocaleDateString('pt-BR')}.
          </p>
        ))}
      </section>

      {aviso && (
        <p
          role="status"
          className={`text-sm ${aviso.tipo === 'ok' ? 'text-green-700 dark:text-green-400' : 'text-red-600'}`}
        >
          {aviso.texto}
        </p>
      )}

      <Link to="/privacidade" className="block text-xs text-neutral-500 underline">
        Ler o aviso de privacidade
      </Link>
    </main>
  );
}
